import {
  ContextApiGetRiskContextResRequest,
  DashboardApiGetResourcesByImagesForDashboardRequest,
  InsightV2Pagination,
  RiskContextResponse,
  SunburstDashboard,
  VulnerabilitiesApiGetCveResourcesRequest,
  VulnerabilitiesApiGetPackageResourcesRequest,
  VulnerabilitiesApiVulnerabilityInsightRequest,
} from '@ariksa/inventory-core/api';
import { isEmpty } from 'lodash';
import { call, select } from 'redux-saga/effects';
import { QueryContext } from 'services/utils/QueryContext';
import { takeLatestAction } from 'services/utils/takeLatestAction';
import {
  DefaultApiGetBySeverityDashboardDataRequest,
  DefaultApiGetBySourcesDashboardDataRequest,
  DefaultApiGetCvesByIdRequest,
  DefaultApiGetCvesRequest,
  DefaultApiGetEcrImageListRequest,
  DefaultApiGetVulnerabilityPriortizationDataRequest,
  DefaultApiGetVulnerabilitySeverityCountTrendRequest,
  DefaultApiGetVulnerablePackagesRequest,
  DefaultApiGetVulnerableResourcesCountTrendRequest,
  ItemApiGetPatchRecommendationsForResourceRequest,
} from 'services/Vulnerability/legacyRequestTypes';
import {
  VulnerabilityBySeverityDashboard,
  VulnerabilityCvePage,
  VulnerabilityDashboardBySources,
  VulnerabilityImageDashboard,
  VulnerabilityPatchRecommendationResource,
  VulnerabilityPrioritization,
  VulnerablePackagePage,
} from 'services/Vulnerability/types';

import {
  getScanVaultCVEs,
  getScanVaultPackages,
  getVulnerabilityPrioritization,
  getScanVaultVulnerabilityResourceInsight,
  getVulnerabilityDashboard,
  getScanVaultVulnerableImages,
  getScanVaultResourcePatchRecommendations,
  getScanVaultCVEDetails,
} from 'api/scan-vault/client';
import {
  mapCveHostsToLegacy,
  mapCVEsToLegacy,
  mapDashboardToLegacy,
  mapPackagesToLegacy,
  mapVulnerabilityResourceInsightToLegacy,
} from 'api/scan-vault/legacy';
import { InventoryService } from 'api/services';
import { LegacyScanAnalysis } from 'api/services/legacyScanAnalysis';
import {
  doDownloadVulnerabilityReport,
  doGetAMIVulnerabilityDetails,
  doGetECRContainerImages,
  doGetECRImageContainers,
  doGetEntitySeverity,
  doGetResourceDetails,
  doGetResourceInsight,
  doGetVulnerabilityDetails,
  doGetVulnerabilitySummary,
} from 'containers/ActiveCloudResource/saga';
import { doGetVulnerabilities as doGetResourceVulnerabilityCount } from 'containers/Inventory/CloudInventory/saga';
import { doGetRiskContext } from 'containers/SharedState/saga';
import { doGetPrioritizationFlow } from 'containers/Visibility/Vulnerabilities/Components/Dashboard/PrioritizationFlow/saga';

import { selectVulnerabilities } from './selectors';
import { actions } from './slice';

function* shouldUseScanVault(params: unknown) {
  const requestFlag = (params as any)?.useScanVaultVulnerabilityFindings;
  if (typeof requestFlag === 'boolean') return requestFlag;
  return yield select(state => selectVulnerabilities(state).useVulnerabilityV3);
}

export function* vulnerabilitySaga() {
  yield takeLatestAction(
    actions.getScanVaultDashboard,
    doGetScanVaultDashboard,
  );
  yield takeLatestAction(
    actions.getScanVaultPrioritization,
    doGetScanVaultPrioritization,
  );
  yield takeLatestAction(
    actions.getPrioritizationFlow,
    doGetPrioritizationFlow,
  );
  yield takeLatestAction(actions.getVulnerabilities, doGetVulnerabilities);
  yield takeLatestAction(actions.getEcrImages, doGetEcrImagesVulnerabilities);
  yield takeLatestAction(actions.getCveHosts, doGetCveHosts);
  yield takeLatestAction(actions.getRiskContext, doGetRiskContext);
  yield takeLatestAction(
    actions.getOpenVulnerabilitiesBySeverity,
    doGetVulnerabilitiesBySeverity,
  );
  yield takeLatestAction(
    actions.getResolvedVulnerabilitiesBySeverity,
    doGetVulnerabilitiesBySeverity,
  );
  yield takeLatestAction(
    actions.getVulnerabilitiesBySeverityCounts,
    doGetVulnerabilitiesBySeverityCounts,
  );
  yield takeLatestAction(
    actions.getHostsWithOpenVulnerabilities,
    doGetHostsWithCriticalHighVulnerabilities,
  );
  yield takeLatestAction(
    actions.getHostsWithResolvedVulnerabilities,
    doGetHostsWithCriticalHighVulnerabilities,
  );
  yield takeLatestAction(
    actions.getHostsWithOpenVulnerabilitiesByType,
    doGetHostsWithCriticalHighVulnerabilities,
  );
  yield takeLatestAction(
    actions.getHostsWithResolvedVulnerabilitiesByType,
    doGetHostsWithCriticalHighVulnerabilities,
  );
  yield takeLatestAction(
    actions.getCveVulnerabilities,
    doGetCveVulnerabilities,
  );
  /*yield takeLatestAction(
    actions.getCveHostsVulnerabilities,
    doGetCveHostsVulnerabilities,
  );*/
  yield takeLatestAction(
    actions.getPackageVulnerabilities,
    doGetPackageVulnerabilities,
  );
  yield takeLatestAction(
    actions.getPatchablePackages,
    doGetPackageVulnerabilities,
  );
  yield takeLatestAction(actions.getPackageHosts, doGetPackageHosts);
  yield takeLatestAction(actions.getCVEDetails, doGetCVEDetailsById);
  yield takeLatestAction(
    actions.getVulnerabilityPrioritizationData,
    doGetVulnerabilityPrioritization,
  );
  yield takeLatestAction(
    actions.getCveHostsRiskContext,
    doGetCveHostsRiskContext,
  );
  yield takeLatestAction(
    actions.getVulnerabilitiesBySources,
    doGetVulnerabilitiesBySources,
  );
  yield takeLatestAction(
    actions.getMostDeployedVulnerableImages,
    doGetMostDeployedVulnerableImages,
  );
  yield takeLatestAction(actions.getResourceInsight, doGetResourceInsight);
  yield takeLatestAction(actions.getResourceDetails, doGetResourceDetails);
  yield takeLatestAction(actions.getResourceAlertCount, doGetEntitySeverity);
  yield takeLatestAction(
    actions.getResourceVulnerabilities,
    doGetVulnerabilityDetails,
  );
  yield takeLatestAction(
    actions.getAMIVulnerabilities,
    doGetAMIVulnerabilityDetails,
  );
  yield takeLatestAction(
    actions.getResourceVulnerabilityCount,
    doGetResourceVulnerabilityCount,
  );
  yield takeLatestAction(
    actions.getResourceVulnerabilitySummary,
    doGetVulnerabilitySummary,
  );
  yield takeLatestAction(actions.getECRImages, doGetECRContainerImages);
  yield takeLatestAction(actions.downloadReport, doDownloadVulnerabilityReport);
  yield takeLatestAction(
    actions.getECRImageContainers,
    doGetECRImageContainers,
  );
  yield takeLatestAction(actions.getContainerRiskContext, doGetRiskContext);
  yield takeLatestAction(
    actions.getPatchRecommendations,
    doGetPatchRecommendations,
  );
}

export function* doGetVulnerabilities(
  ctx: QueryContext<
    InsightV2Pagination,
    VulnerabilitiesApiVulnerabilityInsightRequest
  >,
) {
  const useScanVault = yield call(shouldUseScanVault, ctx.params);
  if (useScanVault) {
    const request = ctx.params as any;
    const legacy = request.vulnerabilityInsightRequest ?? request;
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultVulnerabilityResourceInsight({
          resource_types: legacy.agnostic_resource
            ? [legacy.agnostic_resource]
            : undefined,
          account_ids: legacy.account_id,
          environment_ids: legacy.environment_id
            ? [legacy.environment_id]
            : undefined,
          search: legacy.search_term,
          status: legacy.resolved ? 'resolved' : 'open',
          from: legacy.resolved_at ?? legacy.effective_start,
          patchable: legacy.is_patchable,
          known_exploited: legacy.known_exploited,
          exploitable: legacy.exploitable,
          internet_exposed: legacy.internet_exposed_vm,
          // ScanVault stores severities in lowercase.
          severities: (!isEmpty(legacy.severities)
            ? legacy.severities
            : legacy.severity
            ? [legacy.severity]
            : undefined
          )?.map((value: string) => value.toLowerCase()),
          deployed_in: legacy.deployed_in,
          page: legacy.page,
          size: legacy.size,
        }),
      {
        map: value =>
          mapVulnerabilityResourceInsightToLegacy(
            value,
            legacy.page,
            legacy.size,
          ),
        errorMsg: 'Failed to get vulnerabilities!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => InventoryService.Vulnerability.vulnerabilityInsight(ctx.params),
    { errorMsg: 'Failed to get vulnerabilities!' },
  );
}

export function* doGetEcrImagesVulnerabilities(
  ctx: QueryContext<
    VulnerabilityImageDashboard,
    DefaultApiGetEcrImageListRequest
  >,
) {
  if (yield call(shouldUseScanVault, ctx.params)) {
    yield call(
      ctx.fetch as any,
      () => getScanVaultVulnerableImages(ctx.params as any),
      { map: value => value, errorMsg: 'Failed to get vulnerabilities!' },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getEcrImageList(ctx.params),
    { errorMsg: 'Failed to get vulnerabilities!' },
  );
}

export function* doGetPatchRecommendations(
  ctx: QueryContext<
    VulnerabilityPatchRecommendationResource,
    ItemApiGetPatchRecommendationsForResourceRequest
  >,
) {
  const useScanVault = yield call(shouldUseScanVault, ctx.params);
  const request = ctx.params as any;
  if (useScanVault && request?.resourceUuid) {
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultResourcePatchRecommendations({
          resourceUuid: request.resourceUuid,
          exploitable: request.exploitable,
          severity: request.severity,
          status: request.resolved ? 'resolved' : 'open',
        }),
      {
        errorMsg: 'Failed to get patch recommendations!',
        map: value => value,
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Item.getPatchRecommendationsForResource(ctx.params),
    { errorMsg: 'Failed to get patch recommendations!' },
  );
}

export function* doGetCveVulnerabilities(
  ctx: QueryContext<VulnerabilityCvePage, DefaultApiGetCvesRequest>,
) {
  const useScanVault = yield call(shouldUseScanVault, ctx.params);
  if (useScanVault) {
    const vulnerabilityState = yield select(selectVulnerabilities);
    const resolved =
      (ctx.params as any)?.resolved === true ||
      vulnerabilityState.vulnerabilityType === 'resolved';
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultCVEs({
          ...(ctx.params as any),
          status: resolved ? 'resolved' : 'open',
          from:
            (ctx.params as any)?.resolvedAt ??
            (ctx.params as any)?.resolved_at ??
            (ctx.params as any)?.effectiveStart ??
            (ctx.params as any)?.effective_start ??
            (ctx.params as any)?.publishedAt,
          limit:
            (ctx.params as any)?.size ??
            (ctx.params as any)?.pageSize ??
            (ctx.page as any)?.page_size,
        }),
      {
        map: value => mapCVEsToLegacy(value),
        errorMsg: 'Failed to get vulnerabilities!',
      },
    );
    return;
  }
  yield call(ctx.fetch, () => LegacyScanAnalysis.Default.getCves(ctx.params), {
    errorMsg: 'Failed to get vulnerabilities!',
  });
}

export function* doGetPackageVulnerabilities(
  ctx: QueryContext<
    VulnerablePackagePage,
    DefaultApiGetVulnerablePackagesRequest
  >,
) {
  const useScanVault = yield call(shouldUseScanVault, ctx.params);
  if (useScanVault) {
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultPackages({
          ...(ctx.params as any),
          status: (ctx.params as any)?.resolved ? 'resolved' : 'open',
          from:
            (ctx.params as any)?.resolvedAt ??
            (ctx.params as any)?.resolved_at ??
            (ctx.params as any)?.effectiveStart ??
            (ctx.params as any)?.effective_start,
          limit:
            (ctx.params as any)?.size ??
            (ctx.params as any)?.pageSize ??
            (ctx.page as any)?.page_size,
          patchable: (ctx.params as any)?.isPatchable,
          isPatchable: (ctx.params as any)?.isPatchable,
          severity: (ctx.params as any)?.severity,
          searchTerm: (ctx.params as any)?.searchTerm,
          knownExploited: (ctx.params as any)?.knownExploited,
          exploitable: (ctx.params as any)?.exploitable,
        }),
      {
        map: value => mapPackagesToLegacy(value),
        errorMsg: 'Failed to get vulnerabilities!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getVulnerablePackages(ctx.params),
    { errorMsg: 'Failed to get vulnerabilities!' },
  );
}

export function* doGetCveHosts(
  ctx: QueryContext<
    InsightV2Pagination,
    VulnerabilitiesApiGetCveResourcesRequest
  >,
) {
  if (yield call(shouldUseScanVault, ctx.params)) {
    const params = ctx.params as any;
    const page = Math.max(Number(params.page) || 1, 1);
    const size = Math.max(Number(params.size) || 10, 1);
    yield call(
      ctx.fetch as any,
      () =>
        // Resource insight pages by cursor, so fetch through the requested
        // page and slice it out.
        getScanVaultVulnerabilityResourceInsight({
          cve_ids: [params.cveId],
          environment_ids: params.environmentId
            ? [params.environmentId]
            : undefined,
          status: params.resolved ? 'resolved' : 'open',
          from: params.resolvedAt ?? params.effectiveStart,
          patchable: params.isPatchable,
          package_categories: params.packageCategory
            ? [params.packageCategory]
            : undefined,
          size: Math.min(page * size, 1000),
        }),
      {
        map: value => mapCveHostsToLegacy(value, page, size),
        errorMsg: 'Failed to get CVE Hosts!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => InventoryService.Vulnerability.getCveResources(ctx.params),
    { errorMsg: 'Failed to get CVE Hosts!' },
  );
}

export function* doGetPackageHosts(
  ctx: QueryContext<
    InsightV2Pagination,
    VulnerabilitiesApiGetPackageResourcesRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Vulnerability.getPackageResources(ctx.params),
    { errorMsg: 'Failed to get package Hosts!' },
  );
}

export function* doGetVulnerabilitiesBySeverity(
  ctx: QueryContext<any, DefaultApiGetVulnerabilitySeverityCountTrendRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerabilitySeverityCountTrend(ctx.params),
    { errorMsg: 'Failed to get vulnerabilities by severity!' },
  );
}

export function* doGetVulnerabilitiesBySeverityCounts(
  ctx: QueryContext<
    VulnerabilityBySeverityDashboard,
    DefaultApiGetBySeverityDashboardDataRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getBySeverityDashboardData(ctx.params),
    { errorMsg: 'Failed to get vulnerabilities by severity!' },
  );
}

export function* doGetHostsWithCriticalHighVulnerabilities(
  ctx: QueryContext<any, DefaultApiGetVulnerableResourcesCountTrendRequest>,
) {
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerableResourcesCountTrend(ctx.params),
    { errorMsg: 'Failed to get host vulnerabilities!' },
  );
}

export function* doGetCveHostsRiskContext(
  ctx: QueryContext<
    Record<string, RiskContextResponse[]>,
    ContextApiGetRiskContextResRequest
  >,
) {
  yield call(
    ctx.fetch,
    () => InventoryService.Context.getRiskContextRes(ctx.params),
    {
      errorMsg: 'Failed to get risk context!',
    },
  );
}

export function* doGetCVEDetailsById(
  ctx: QueryContext<VulnerabilityCvePage, DefaultApiGetCvesByIdRequest>,
) {
  const useScanVault = yield call(shouldUseScanVault, ctx.params);
  if (useScanVault) {
    const request = (ctx.params as any)?.cVEByIDRequest ?? {};
    yield call(
      ctx.fetch as any,
      () =>
        getScanVaultCVEDetails({
          cveIds: request.vulnerability_ids,
          packageName: request.package_name,
          packageVersion: request.package_version,
          search: (ctx.params as any)?.searchTerm,
          severity: (ctx.params as any)?.severity,
          status: (ctx.params as any)?.resolved ? 'resolved' : 'open',
        }),
      {
        errorMsg: 'Failed to get vulnerability details!',
        map: value => value,
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getCvesById(ctx.params),
    {
      errorMsg: 'Failed to get data scanner info!',
    },
  );
}

export function* doGetVulnerabilityPrioritization(
  ctx: QueryContext<
    VulnerabilityPrioritization,
    DefaultApiGetVulnerabilityPriortizationDataRequest
  >,
) {
  if (yield call(shouldUseScanVault, ctx.params)) {
    yield call(
      ctx.fetch as any,
      () => getVulnerabilityPrioritization(ctx.params as any),
      {
        map: value => ({
          ...value,
          internet_exposed_resources_having_patchable_packages_with_explitable_cve:
            value.internet_exposed_resources_having_patchable_packages_with_exploitable_cve,
        }),
        errorMsg: 'Failed to get vulnerability prioritization data!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () =>
      LegacyScanAnalysis.Default.getVulnerabilityPriortizationData(ctx.params),
    {
      errorMsg: 'Failed to get vulnerability prioritization data!',
    },
  );
}

export function* doGetVulnerabilitiesBySources(
  ctx: QueryContext<
    VulnerabilityDashboardBySources,
    DefaultApiGetBySourcesDashboardDataRequest
  >,
) {
  if (yield call(shouldUseScanVault, ctx.params)) {
    yield call(
      ctx.fetch as any,
      () => getVulnerabilityDashboard(ctx.params as any),
      {
        map: response =>
          mapDashboardToLegacy(response).vulnerabilitiesBySources,
        errorMsg: 'Failed to get vulnerabilities by sources!',
      },
    );
    return;
  }
  yield call(
    ctx.fetch,
    () => LegacyScanAnalysis.Default.getBySourcesDashboardData(ctx.params),
    {
      errorMsg: 'Failed to get vulnerabilities by sources!',
    },
  );
}

export function* doGetMostDeployedVulnerableImages(
  ctx: QueryContext<
    SunburstDashboard,
    DashboardApiGetResourcesByImagesForDashboardRequest
  >,
) {
  yield call(
    ctx.fetch,
    () =>
      InventoryService.Dashboard.getResourcesByImagesForDashboard(ctx.params),
    {
      errorMsg: 'Failed to get most deployed vulnerable images!',
    },
  );
}

export function* doGetScanVaultDashboard(ctx: QueryContext<any, any>) {
  yield call(ctx.fetch, () => getVulnerabilityDashboard(ctx.params as any), {
    map: value => value,
    errorMsg: 'Failed to get vulnerability dashboard!',
  });
}

export function* doGetScanVaultPrioritization(ctx: QueryContext<any, any>) {
  yield call(
    ctx.fetch,
    () => getVulnerabilityPrioritization(ctx.params as any),
    {
      map: value => value,
      errorMsg: 'Failed to get vulnerability prioritization data!',
    },
  );
}
